import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, addDays, startOfToday } from "date-fns";
import { it } from "date-fns/locale";
import {
  Calendar,
  Clock,
  Mail,
  CalendarX,
  LogOut,
  Users,
  MessageSquare,
  BarChart3,
  Check,
  X,
  Trash2,
  Eye,
  RefreshCw,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cafm-restructure/artifacts/o51eebue_597817161_1172388751673908_7998470242494796850_n.jpg";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const statusLabels = {
  pending: "In attesa",
  confirmed: "Confermato",
  cancelled: "Annullato",
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [selectedTab, setSelectedTab] = useState("overview");
  const [dateToBlock, setDateToBlock] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [editScheduleForm, setEditScheduleForm] = useState({
    start_time: "",
    end_time: "",
    break_start: "",
    break_end: "",
  });

  const token = localStorage.getItem("adminToken");

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    verifyAndLoad();
  }, []);

  const verifyAndLoad = async () => {
    try {
      await axios.get(`${API}/admin/verify`, { headers });
      loadData();
    } catch (error) {
      localStorage.removeItem("adminToken");
      navigate("/admin");
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, appointmentsRes, messagesRes, blockedRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/appointments`, { headers }),
        axios.get(`${API}/admin/messages`, { headers }),
        axios.get(`${API}/admin/blocked-dates`, { headers }),
        axios.get(`${API}/schedule/weekly`),
      ]);
      
      setStats(statsRes.data);
      setAppointments(appointmentsRes.data);
      setMessages(messagesRes.data);
      setBlockedDates(blockedRes.data);
      setSchedule(scheduleRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success("Dati aggiornati");
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { headers });
    } catch (error) {
      // Ignore errors
    }
    localStorage.removeItem("adminToken");
    navigate("/admin");
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}/status?status=${status}`, {}, { headers });
      toast.success(`Appuntamento ${statusLabels[status].toLowerCase()}`);
      loadData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo appuntamento?")) return;
    
    try {
      await axios.delete(`${API}/admin/appointments/${id}`, { headers });
      toast.success("Appuntamento eliminato");
      loadData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const markMessageRead = async (id) => {
    try {
      await axios.put(`${API}/admin/messages/${id}/read`, {}, { headers });
      loadData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo messaggio?")) return;
    
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers });
      toast.success("Messaggio eliminato");
      loadData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const blockDate = async () => {
    if (!dateToBlock) return;
    
    try {
      await axios.post(`${API}/admin/blocked-dates`, {
        date: format(dateToBlock, "yyyy-MM-dd"),
        reason: blockReason || "Chiuso"
      }, { headers });
      toast.success("Data bloccata");
      setDateToBlock(null);
      setBlockReason("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const unblockDate = async (dateStr) => {
    try {
      await axios.delete(`${API}/admin/blocked-dates/${dateStr}`, { headers });
      toast.success("Data sbloccata");
      loadData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const updateSchedule = async (dayOfWeek, isWorking) => {
    try {
      await axios.put(`${API}/schedule/weekly/${dayOfWeek}`, {
        day_of_week: dayOfWeek,
        is_working_day: isWorking
      });
      toast.success("Orario aggiornato");
      loadData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const openEditSchedule = (day) => {
    setEditingDay(day);
    setEditScheduleForm({
      start_time: day.start_time || "09:00",
      end_time: day.end_time || "18:00",
      break_start: day.break_start || "13:00",
      break_end: day.break_end || "14:00",
    });
  };

  const saveScheduleChanges = async () => {
    if (!editingDay) return;
    
    try {
      await axios.put(`${API}/schedule/weekly/${editingDay.day_of_week}`, {
        day_of_week: editingDay.day_of_week,
        is_working_day: true,
        start_time: editScheduleForm.start_time,
        end_time: editScheduleForm.end_time,
        break_start: editScheduleForm.break_start || null,
        break_end: editScheduleForm.break_end || null,
      });
      toast.success(`Orari di ${editingDay.day_name} aggiornati`);
      setEditingDay(null);
      loadData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Logo" className="h-10 w-auto" />
              <div>
                <h1 className="font-heading font-bold text-slate-900">Pannello Admin</h1>
                <p className="text-xs text-slate-500">CAF & Patronato EPAS</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={refreshData}
                disabled={refreshing}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                Aggiorna
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-appointments">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-900" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Appuntamenti Oggi</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.appointments?.today || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-pending">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">In Attesa</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.appointments?.pending || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-messages">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-red-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Messaggi Non Letti</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.messages?.unread || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="stat-blocked">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <CalendarX className="w-6 h-6 text-slate-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Date Bloccate</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.blocked_dates || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="appointments" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-lg shadow-sm">
            <TabsTrigger value="appointments" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Appuntamenti
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <MessageSquare className="w-4 h-4 mr-2" />
              Messaggi
            </TabsTrigger>
            <TabsTrigger value="availability" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Disponibilità
            </TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b">
                <h2 className="font-heading font-bold text-lg">Appuntamenti ({appointments.length})</h2>
              </div>
              {appointments.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Nessun appuntamento presente
                </div>
              ) : (
                <div className="divide-y">
                  {appointments.map((apt) => (
                    <div key={apt.id} className="p-4 hover:bg-slate-50" data-testid={`appointment-${apt.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-slate-900">{apt.name}</span>
                            <Badge className={statusColors[apt.status]}>
                              {statusLabels[apt.status]}
                            </Badge>
                          </div>
                          <div className="text-sm text-slate-600 space-y-1">
                            <p><Calendar className="w-4 h-4 inline mr-2" />{format(new Date(apt.date), "EEEE d MMMM yyyy", { locale: it })} alle {apt.time}</p>
                            <p><Mail className="w-4 h-4 inline mr-2" />{apt.email}</p>
                            <p><Clock className="w-4 h-4 inline mr-2" />Servizio: {apt.service}</p>
                            {apt.notes && <p className="text-slate-400">Note: {apt.notes}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {apt.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-600 hover:bg-green-50"
                                onClick={() => updateAppointmentStatus(apt.id, "confirmed")}
                                data-testid={`confirm-${apt.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-600 hover:bg-red-50"
                                onClick={() => updateAppointmentStatus(apt.id, "cancelled")}
                                data-testid={`cancel-${apt.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-red-600"
                            onClick={() => deleteAppointment(apt.id)}
                            data-testid={`delete-apt-${apt.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b">
                <h2 className="font-heading font-bold text-lg">Messaggi ({messages.length})</h2>
              </div>
              {messages.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Nessun messaggio presente
                </div>
              ) : (
                <div className="divide-y">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`p-4 ${!msg.read ? "bg-blue-50" : "hover:bg-slate-50"}`} data-testid={`message-${msg.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-slate-900">{msg.name}</span>
                            {!msg.read && <Badge className="bg-blue-100 text-blue-800">Nuovo</Badge>}
                          </div>
                          <p className="text-sm font-medium text-slate-700 mb-1">{msg.subject}</p>
                          <p className="text-sm text-slate-600 mb-2">{msg.message}</p>
                          <div className="text-xs text-slate-400">
                            {msg.email} {msg.phone && `• ${msg.phone}`}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!msg.read && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => markMessageRead(msg.id)}
                              data-testid={`read-${msg.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-red-600"
                            onClick={() => deleteMessage(msg.id)}
                            data-testid={`delete-msg-${msg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Weekly Schedule */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-heading font-bold text-lg mb-4">Orari Settimanali</h2>
                <p className="text-sm text-slate-500 mb-4">Clicca su "Modifica" per cambiare gli orari di apertura e pausa</p>
                <div className="space-y-3">
                  {schedule.map((day) => (
                    <div key={day.day_of_week} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <span className="font-medium">{day.day_name}</span>
                        {day.is_working_day && (
                          <div className="text-sm text-slate-500">
                            {day.start_time} - {day.end_time}
                            {day.break_start && day.break_end && (
                              <span className="ml-2 text-slate-400">(Pausa: {day.break_start} - {day.break_end})</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {day.is_working_day && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditSchedule(day)}
                            data-testid={`edit-day-${day.day_of_week}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={day.is_working_day ? "default" : "outline"}
                          className={day.is_working_day ? "bg-green-600 hover:bg-green-700" : ""}
                          onClick={() => updateSchedule(day.day_of_week, !day.is_working_day)}
                          data-testid={`toggle-day-${day.day_of_week}`}
                        >
                          {day.is_working_day ? "Aperto" : "Chiuso"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Edit Schedule Dialog */}
                <Dialog open={!!editingDay} onOpenChange={(open) => !open && setEditingDay(null)}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Modifica Orari - {editingDay?.day_name}</DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Apertura</Label>
                          <Input
                            type="time"
                            value={editScheduleForm.start_time}
                            onChange={(e) => setEditScheduleForm({...editScheduleForm, start_time: e.target.value})}
                            data-testid="edit-start-time"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Chiusura</Label>
                          <Input
                            type="time"
                            value={editScheduleForm.end_time}
                            onChange={(e) => setEditScheduleForm({...editScheduleForm, end_time: e.target.value})}
                            data-testid="edit-end-time"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Inizio Pausa</Label>
                          <Input
                            type="time"
                            value={editScheduleForm.break_start}
                            onChange={(e) => setEditScheduleForm({...editScheduleForm, break_start: e.target.value})}
                            data-testid="edit-break-start"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Fine Pausa</Label>
                          <Input
                            type="time"
                            value={editScheduleForm.break_end}
                            onChange={(e) => setEditScheduleForm({...editScheduleForm, break_end: e.target.value})}
                            data-testid="edit-break-end"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-400">Lascia vuoti i campi pausa se non c'è pausa pranzo</p>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setEditingDay(null)}>
                        Annulla
                      </Button>
                      <Button onClick={saveScheduleChanges} data-testid="save-schedule-btn">
                        Salva Modifiche
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Blocked Dates */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-bold text-lg">Date Bloccate</h2>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="add-blocked-date">
                        <Plus className="w-4 h-4 mr-2" />
                        Blocca Data
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Blocca una Data</DialogTitle>
                      </DialogHeader>
                      <div className="py-4">
                        <CalendarComponent
                          mode="single"
                          selected={dateToBlock}
                          onSelect={setDateToBlock}
                          locale={it}
                          disabled={(date) => date < startOfToday()}
                          className="rounded-md border mx-auto"
                        />
                        <div className="mt-4">
                          <Input
                            placeholder="Motivo (opzionale)"
                            value={blockReason}
                            onChange={(e) => setBlockReason(e.target.value)}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button onClick={blockDate} disabled={!dateToBlock}>
                          Blocca
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                
                {blockedDates.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nessuna data bloccata</p>
                ) : (
                  <div className="space-y-2">
                    {blockedDates.map((blocked) => (
                      <div key={blocked.date} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <span className="font-medium text-red-900">
                            {format(new Date(blocked.date), "EEEE d MMMM yyyy", { locale: it })}
                          </span>
                          {blocked.reason && (
                            <span className="text-sm text-red-700 ml-2">({blocked.reason})</span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => unblockDate(blocked.date)}
                          data-testid={`unblock-${blocked.date}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
